/**
 * Reporting periods, as the UI holds them.
 *
 * A period is either one of the server's named ranges or an explicit pair of
 * dates. Both are already understood by `resolveWindow` on the API side — every
 * windowed endpoint takes `range` OR `from`/`to` — so nothing here needs a new
 * server capability; the picker simply never offered the second form.
 *
 * The rules below mirror `domain/dateWindow.ts` exactly. They are duplicated
 * rather than shared because the server must enforce them regardless of what the
 * client sends; the copy here exists to say so before a round trip, not instead
 * of one.
 */

/**
 * The four periods that earn a permanent chip.
 *
 * There were eight, and eight chips plus a Custom button is a wall of buttons
 * rather than a control: past four or so options the eye stops reading them as a
 * set and starts scanning them as a list. Everything else moved behind the
 * calendar button, which is where an explicit window already lived.
 *
 * These four are the ones a shopkeeper reaches for without thinking. A month is
 * a deliberate act — closing off VAT, checking last month against this one — and
 * it can afford the extra click.
 */
export const PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'last7', label: '7 days' },
  { key: 'last30', label: '30 days' },
] as const;

/**
 * Offered inside the picker rather than as chips.
 *
 * Kept as *named* ranges rather than as from/to shortcuts, which would have been
 * the easier implementation. The server gives a named month the previous month
 * to compare against; an equivalent custom window gets "the preceding 31 days",
 * which is a different and worse comparison. The name carries meaning the dates
 * do not.
 */
export const IN_PICKER = [
  { key: 'thisMonth', label: 'This month' },
  { key: 'lastMonth', label: 'Last month' },
] as const;

export type PresetKey =
  | (typeof PRESETS)[number]['key']
  | (typeof IN_PICKER)[number]['key'];

const LABELS = new Map<string, string>(
  [...PRESETS, ...IN_PICKER].map((option) => [option.key, option.label]),
);

/** True for the periods that have their own chip, so the picker knows what to show. */
export function hasChip(value: RangeValue): boolean {
  return PRESETS.some((option) => option.key === value.key);
}

export type RangeValue = { key: PresetKey } | { key: 'custom'; from: string; to: string };

/** Matches `MAX_SPAN_DAYS` in the API. */
export const MAX_SPAN_DAYS = 400;

export function preset(key: PresetKey): RangeValue {
  return { key };
}

export function isCustom(
  value: RangeValue,
): value is { key: 'custom'; from: string; to: string } {
  return value.key === 'custom';
}

/** The query string fragment, without a leading `?` or `&`. */
export function rangeQuery(value: RangeValue): string {
  return isCustom(value)
    ? `from=${value.from}&to=${value.to}`
    : `range=${value.key}`;
}

/**
 * A stable react-query cache key.
 *
 * Must distinguish two different custom windows, which `value.key` alone does
 * not — without the dates in here, changing the custom range would serve the
 * previous one from cache and look like the picker had failed.
 */
export function rangeKey(value: RangeValue): string {
  return isCustom(value) ? `custom:${value.from}:${value.to}` : value.key;
}

export function rangeLabel(value: RangeValue): string {
  if (isCustom(value)) {
    return value.from === value.to ? value.from : `${value.from} → ${value.to}`;
  }
  return LABELS.get(value.key) ?? value.key;
}

/** Today in the browser's timezone, as `YYYY-MM-DD`. */
export function todayISO(): string {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

const DATE_STRING = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Why a from/to pair cannot be used, or null if it can.
 *
 * The store's timezone is not applied here: these are calendar dates, and the
 * server resolves them into instants in the store's own zone. Comparing the
 * strings is therefore both sufficient and correct.
 */
export function validateCustom(from: string, to: string): string | null {
  if (!DATE_STRING.test(from) || !DATE_STRING.test(to)) {
    return 'Pick both a start and an end date.';
  }
  if (from > to) return 'The start date must not be after the end date.';

  const spanDays =
    Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1;
  if (spanDays > MAX_SPAN_DAYS) {
    return `That covers ${spanDays} days; the most a single report can cover is ${MAX_SPAN_DAYS}.`;
  }
  return null;
}
